import { ART_PAL, body, edge, fade, gloss, mix, n, shade, shadowInk, star, tint, warm, type ArtPal, type ArtTheme } from './covers/kit';

/*
 * Golden Flower (炸金花) playing cards in the "premium casual" style of the covers (art/covers/kit.ts): soft volume,
 * a highlight top-left, a soft shadow below, neat outlines. No casino gloss: no felt, no chips, no portraits on
 * J/Q/K (a gold ring instead), no neon. The face is warm ivory (a tint of the palette gold — never pure white, and
 * spades/clubs are deep slate, not black: no white-and-black mourning pair). Ranks are vector strokes, not <text>;
 * the card name for screen readers comes from i18n (`zhajinhua.card`). The back is lacquer red with a gold
 * lattice and a five-petal golden flower (金花) — no glyphs, no 福.
 * Card id: rank = id >> 2 (0 = '2' … 12 = 'A'), suit = id & 3 (0 ♠, 1 ♥, 2 ♣, 3 ♦).
 * Dou Dizhu (斗地主) adds the jokers: 52 = small joker (小王), 53 = big joker (大王) — see `jokerPiece`.
 */

/** Card box (viewBox) — the card itself is 92 × 128 at (4, 2), the rest is its shadow. */
export const CARD_BOX = { w: 100, h: 140 } as const;

/** Rank glyphs, strokes in a 10 × 14 box; index = rank. */
export const RANK_GLYPHS: readonly string[] = [
  'M1.5 3.5Q1.5 0 5 0Q8.5 0 8.5 3.5Q8.5 6 1 14H9', // 2
  'M1.5 1H8.5L4.5 6Q9 6 9 10Q9 14 5 14Q2 14 1 11.5', // 3
  'M7 14V0L1 10H9.5', // 4
  'M8.5 0H2L1.5 6.5Q3 5.5 5 5.5Q9 5.5 9 9.8Q9 14 5 14Q2 14 1 11.5', // 5
  'M8 1Q6.5 0 5 0Q1 0 1 7.5V9.5Q1 14 5 14Q9 14 9 9.8Q9 5.8 5 5.8Q2.5 5.8 1 7.5', // 6
  'M1 0H9L3.5 14', // 7
  'M5 6.3Q1.5 6.3 1.5 3.2Q1.5 0 5 0Q8.5 0 8.5 3.2Q8.5 6.3 5 6.3Q1 6.3 1 10.1Q1 14 5 14Q9 14 9 10.1Q9 6.3 5 6.3Z', // 8
  'M9 6.5Q7.5 8.2 5 8.2Q1 8.2 1 4.2Q1 0 5 0Q9 0 9 4.5V6.5Q9 14 5 14Q3.5 14 2 13', // 9
  'M0.3 2.2L2 0.6V14M7 0Q4.8 0 4.8 7Q4.8 14 7 14Q9.2 14 9.2 7Q9.2 0 7 0Z', // 10
  'M3 0H9M7 0V10Q7 14 4 14Q1 14 1 11', // J
  'M5 0Q1 0 1 6.5Q1 13 5 13Q9 13 9 6.5Q9 0 5 0ZM6 10L9.5 14', // Q
  'M1.5 0V14M8.5 0L1.5 8M4 5.5L9 14', // K
  'M1 14L5 0L9 14M2.6 9H7.4', // A
];

/** Suit pips in a 20 × 20 box, filled with `c`: 0 ♠, 1 ♥, 2 ♣, 3 ♦. */
export function suitPip(suit: number, c: string): string {
  switch (suit) {
    case 0:
      return `<path d="M10 1C4 6.5 1.5 9 1.5 12C1.5 14.5 3.4 16.2 5.6 16.2C7.2 16.2 8.4 15.4 9.2 14.2C8.9 16.2 8 17.8 6.5 19H13.5C12 17.8 11.1 16.2 10.8 14.2C11.6 15.4 12.8 16.2 14.4 16.2C16.6 16.2 18.5 14.5 18.5 12C18.5 9 16 6.5 10 1Z" fill="${c}"/>`;
    case 1:
      return `<path d="M10 18C3 12.5 1 9.5 1 6.2C1 3.3 3.2 1.2 5.8 1.2C7.6 1.2 9 2.2 10 3.8C11 2.2 12.4 1.2 14.2 1.2C16.8 1.2 19 3.3 19 6.2C19 9.5 17 12.5 10 18Z" fill="${c}"/>`;
    case 2:
      return `<g fill="${c}"><circle cx="10" cy="5.5" r="4.2"/><circle cx="5.2" cy="11.5" r="4.2"/><circle cx="14.8" cy="11.5" r="4.2"/><path d="M9 11H11L12.5 19H7.5Z"/></g>`;
    default:
      return `<path d="M10 1L17 10L10 19L3 10Z" fill="${c}"/>`;
  }
}

/** Suit colour: hearts / diamonds red, spades / clubs deep slate (both themes: the face is always light). */
export const suitColor = (suit: number, P: ArtPal): string => (suit === 1 || suit === 3 ? P.red : shade(P.slate, 0.15));

/** Warm ivory of the face (a tint of the palette gold). */
const ivory = (P: ArtPal): string => tint(P.gold, P.dark ? 0.86 : 0.9);

/** Petal gold: the light-theme gold reads brown on the ivory face, so it is lifted there. */
const petalGold = (P: ArtPal): string => (P.dark ? P.gold : tint(P.gold, 0.42));

/** Five-petal golden flower (金花) centred on (0, 0), radius ~r (needs the `${p}gd` petal gradient). */
export function goldenFlower(P: ArtPal, p: string, r: number): string {
  let petals = '';
  for (let i = 0; i < 5; i++) {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / 5;
    petals += `<ellipse cx="${n(Math.cos(a) * r * 0.55)}" cy="${n(Math.sin(a) * r * 0.55)}" rx="${n(r * 0.42)}" ry="${n(r * 0.3)}" transform="rotate(${n((a * 180) / Math.PI)} ${n(Math.cos(a) * r * 0.55)} ${n(Math.sin(a) * r * 0.55)})"/>`;
  }
  return (
    `<g fill="url(#${p}gd)" stroke="${edge(petalGold(P), P)}" stroke-width="1">${petals}</g>` +
    `<circle r="${n(r * 0.26)}" fill="${tint(warm(P), 0.5)}" stroke="${edge(petalGold(P), P)}" stroke-width="1"/>`
  );
}

function frame(P: ArtPal, p: string, fillId: string, stroke: string): string {
  return (
    `<ellipse cx="50" cy="134" rx="40" ry="4.5" fill="url(#${p}sh)"/>` +
    `<rect x="4" y="2" width="92" height="128" rx="9" fill="url(#${fillId})" stroke="${stroke}" stroke-width="1.5"/>`
  );
}

/** Face of card `id`. */
function facePiece(P: ArtPal, p: string, id: number): string {
  const rank = id >> 2;
  const suit = id & 3;
  const c = suitColor(suit, P);
  const face = ivory(P);
  const glyph = `<path d="${RANK_GLYPHS[rank]}" fill="none" stroke="${c}" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>`;
  const corner = `<g transform="translate(11 9) scale(1.25)">${glyph}</g><g transform="translate(9.5 29) scale(.62)">${suitPip(suit, c)}</g>`;
  let centre: string;
  if (rank >= 9 && rank <= 11) {
    // J / Q / K: a gold ring with the pip — no portraits
    centre =
      `<circle cx="50" cy="66" r="25" fill="${tint(warm(P), 0.72)}" stroke="${warm(P)}" stroke-width="2.5"/>` +
      `<circle cx="50" cy="66" r="20" fill="none" stroke="${warm(P)}" stroke-opacity=".6" stroke-width="1"/>` +
      `<g transform="translate(35 51) scale(1.5)">${suitPip(suit, c)}</g>`;
  } else if (rank === 12) {
    // Ace: the big pip on a golden flower
    centre = `<g transform="translate(50 66)">${goldenFlower(P, p, 30)}</g><g transform="translate(33 49) scale(1.7)">${suitPip(suit, c)}</g>`;
  } else {
    centre = `<g transform="translate(30 46) scale(2)">${suitPip(suit, c)}</g>`;
  }
  return (
    `<defs>${body(`${p}f`, face, 0.6, 0.1, 0.6, 1)}${fade(`${p}sh`, shadowInk(P), P.dark ? 0.5 : 0.18)}${body(`${p}gd`, petalGold(P), 0.45, 0.22)}</defs>` +
    frame(P, p, `${p}f`, shade(mix(face, P.gold, 0.45), 0.12)) +
    // the blick sits under the indices so it never washes a rank out
    gloss(26, 16, 15, 5, -30, 0.35) +
    `<rect x="9" y="7" width="82" height="118" rx="6" fill="none" stroke="${warm(P)}" stroke-opacity=".35" stroke-width="1"/>` +
    corner +
    `<g transform="rotate(180 50 66)">${corner}</g>` +
    centre
  );
}

/** Small joker (小王) id. */
export const SMALL_JOKER = 52;
/** Big joker (大王) id. */
export const BIG_JOKER = 53;

/**
 * Joker face (52 small / 53 big): a crown-sun — ten rays around a disc with a five-point crown in front — without any
 * letters. The big joker is full colour (red disc, gold rays and crown, red gems), the small one the same shape in slate.
 * The corner index (the only part seen when the cards overlap in a fan: ~22 of the 100 units) is a five-point star over
 * a vertical bar: solid red with a gold rim on the big joker, a hollow slate outline on the small one — told apart by
 * shape, not only by colour.
 */
function jokerPiece(P: ArtPal, p: string, big: boolean): string {
  const face = ivory(P);
  const slate = shade(P.slate, 0.15);
  const gold = big ? petalGold(P) : tint(P.slate, 0.62);
  const disc = big ? P.red : tint(P.slate, 0.38);
  const ink = big ? edge(P.red, P) : slate;
  const gem = big ? P.red : face;
  let rays = '';
  for (let i = 0; i < 10; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    const s = (r: number, da: number) => `${n(50 + Math.cos(a + da) * r)} ${n(55 + Math.sin(a + da) * r)}`;
    rays += `M${s(19, -0.16)}L${s(33, 0)}L${s(19, 0.16)}Z`;
  }
  const crown = 'M29 88L27 66L34.5 76L38 60L44.5 74L50 56L55.5 74L62 60L65.5 76L73 66L71 88Z';
  const tips: [number, number][] = [
    [27, 65],
    [38, 59],
    [50, 55],
    [62, 59],
    [73, 65],
  ];
  const corner = big
    ? `<polygon points="${star(16, 17, 7.5, 3.2, 5)}" fill="${P.red}" stroke="${edge(petalGold(P), P)}" stroke-width="1.2" stroke-linejoin="round"/>` +
      `<rect x="13.5" y="28" width="5" height="26" rx="2.5" fill="url(#${p}jb)" stroke="${edge(P.red, P)}" stroke-width=".8"/>`
    : `<polygon points="${star(16, 17, 7.5, 3.2, 5)}" fill="none" stroke="${slate}" stroke-width="2" stroke-linejoin="round"/>` +
      `<rect x="14" y="28.5" width="4" height="25" rx="2" fill="none" stroke="${slate}" stroke-width="1.6"/>`;
  return (
    `<defs>${body(`${p}f`, face, 0.6, 0.1, 0.6, 1)}${fade(`${p}sh`, shadowInk(P), P.dark ? 0.5 : 0.18)}` +
    `${body(`${p}gd`, gold, 0.45, 0.22)}${body(`${p}ds`, disc, 0.35, 0.3)}${fade(`${p}gw`, big ? warm(P) : P.slate, big ? 0.55 : 0.25)}` +
    (big ? `<linearGradient id="${p}jb" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${P.red}"/><stop offset="1" stop-color="${petalGold(P)}"/></linearGradient>` : '') +
    `</defs>` +
    frame(P, p, `${p}f`, shade(mix(face, big ? P.gold : P.slate, 0.45), 0.12)) +
    gloss(26, 16, 15, 5, -30, 0.35) +
    `<rect x="9" y="7" width="82" height="118" rx="6" fill="none" stroke="${big ? warm(P) : P.slate}" stroke-opacity=".35" stroke-width="1"/>` +
    corner +
    `<g transform="rotate(180 50 66)">${corner}</g>` +
    `<circle cx="50" cy="62" r="38" fill="url(#${p}gw)"/>` +
    `<path d="${rays}" fill="url(#${p}gd)" stroke="${edge(gold, P)}" stroke-width=".8" stroke-linejoin="round"/>` +
    `<circle cx="50" cy="55" r="17" fill="url(#${p}ds)" stroke="${ink}" stroke-width="1.2"/>` +
    `<ellipse cx="50" cy="102" rx="24" ry="3.5" fill="${shadowInk(P)}" fill-opacity="${P.dark ? 0.18 : 0.1}"/>` +
    `<path d="${crown}" fill="url(#${p}gd)" stroke="${edge(gold, P)}" stroke-width="1.2" stroke-linejoin="round"/>` +
    `<rect x="28" y="86" width="44" height="11" rx="3" fill="url(#${p}gd)" stroke="${edge(gold, P)}" stroke-width="1.2"/>` +
    tips.map(([x, y]) => `<circle cx="${x}" cy="${y}" r="2.6" fill="${gem}" stroke="${edge(gold, P)}" stroke-width=".8"/>`).join('') +
    `<circle cx="50" cy="91.5" r="3.4" fill="${gem}" stroke="${edge(gold, P)}" stroke-width=".8"/>` +
    `<circle cx="38" cy="91.5" r="2" fill="${gem}"/><circle cx="62" cy="91.5" r="2" fill="${gem}"/>` +
    gloss(43, 47, 7, 3, -30, big ? 0.35 : 0.25)
  );
}

/** Back: lacquer red, gold inset border, a diagonal gold lattice and the golden flower medallion. */
function backPiece(P: ArtPal, p: string): string {
  const red = P.red;
  const gold = warm(P);
  let lattice = '';
  for (let k = -128; k <= 92; k += 12) lattice += `M${14 + k} 12L${14 + k + 106} 120M${14 + k + 106} 12L${14 + k} 120`;
  return (
    `<defs>${body(`${p}b`, red, 0.3, 0.35, 0.6, 1)}${fade(`${p}sh`, shadowInk(P), P.dark ? 0.5 : 0.18)}${body(`${p}gd`, petalGold(P), 0.45, 0.22)}` +
    `<clipPath id="${p}cl"><rect x="12" y="10" width="76" height="112" rx="5"/></clipPath></defs>` +
    frame(P, p, `${p}b`, edge(red, P)) +
    `<rect x="9" y="7" width="82" height="118" rx="6" fill="none" stroke="${gold}" stroke-opacity=".75" stroke-width="1.5"/>` +
    `<path d="${lattice}" clip-path="url(#${p}cl)" fill="none" stroke="${gold}" stroke-opacity=".28" stroke-width="1"/>` +
    `<circle cx="50" cy="66" r="24" fill="${shade(red, 0.2)}" stroke="${gold}" stroke-width="2"/>` +
    `<g transform="translate(50 66)">${goldenFlower(P, p, 22)}</g>` +
    `<polygon points="${star(50, 18, 3, 1.2, 4)}" fill="${gold}"/><polygon points="${star(50, 114, 3, 1.2, 4)}" fill="${gold}"/>` +
    gloss(24, 18, 16, 6, -30, 0.3)
  );
}

export type CardArt = number | 'back';

/** Standalone SVG of a card face (0…51, jokers 52 / 53) or the back (ids prefixed per card + theme). */
export function cardSvg(card: CardArt, theme: ArtTheme, prefix = `k${card === 'back' ? 'b' : card}${theme[0]}`): string {
  const P = ART_PAL[theme];
  const inner =
    card === 'back' ? backPiece(P, prefix) : card >= SMALL_JOKER ? jokerPiece(P, prefix, card === BIG_JOKER) : facePiece(P, prefix, card);
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${CARD_BOX.w} ${CARD_BOX.h}" width="${CARD_BOX.w}" height="${CARD_BOX.h}">${inner}</svg>`;
}

const uris = new Map<string, string>();

/** Data URI of a card (cached per card + theme). */
export function cardUri(card: CardArt, theme: ArtTheme): string {
  const key = `${card}${theme}`;
  let u = uris.get(key);
  if (!u) {
    u = 'data:image/svg+xml;utf8,' + encodeURIComponent(cardSvg(card, theme));
    uris.set(key, u);
  }
  return u;
}
